// external modules
import { mapState, mapActions } from 'vuex'

// internal modules
import Storage from '@/utils/Storage'
import '@/assets/scss/intro_video-page.scss'

// components
import BaseIntroReset from '@/components/BaseIntroReset.vue'

export default {
  inject: ['intro_js'],
  components: {
    BaseIntroReset,
  },
  data: {
    intro_timeout: null,
  },
  computed: {
    ...mapState({
      $_intro_key: (state) => state.dashboard.intro.key,
      $_intro_type: (state) => state.dashboard.intro.type,
      $_intro_status: (state) => state.dashboard.intro.status,
    }),
    intro_key: {
      get() {
        return this.$_intro_key
      },
      set(payload) {
        this.setIntroKey(payload)
      },
    },
    intro_type: {
      get() {
        return this.$_intro_type
      },
      set(payload) {
        this.setIntroType(payload)
      },
    },
    intro_status: {
      get() {
        return this.$_intro_status
      },
      set(payload) {
        this.setIntroStatus(payload)
      },
    },
    introJS() {
      return this.intro_js
    },
  },
  beforeDestroy() {
    this.intro_key = null
    this.intro_type = 'intro'
    this.intro_status = 'finished'

    this.hideIntroJS()
  },
  methods: {
    ...mapActions('dashboard', [
      'setIntroKey',
      'setIntroType',
      'setIntroStatus',
    ]),
    handleVideoPageIntroCss() {
      const introHeaderSkipButton = document.querySelector(
        '.introjs-skipbutton'
      )
      const skipIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M18.7071 6.70711C19.0976 6.31658 19.0976 5.68342 18.7071 5.29289C18.3166 4.90237 17.6834 4.90237 17.2929 5.29289L12 10.5858L6.70711 5.29289C6.31658 4.90237 5.68342 4.90237 5.29289 5.29289C4.90237 5.68342 4.90237 6.31658 5.29289 6.70711L10.5858 12L5.29289 17.2929C4.90237 17.6834 4.90237 18.3166 5.29289 18.7071C5.68342 19.0976 6.31658 19.0976 6.70711 18.7071L12 13.4142L17.2929 18.7071C17.6834 19.0976 18.3166 19.0976 18.7071 18.7071C19.0976 18.3166 19.0976 17.6834 18.7071 17.2929L13.4142 12L18.7071 6.70711Z"/>
      </svg>
      `
      introHeaderSkipButton.innerHTML = skipIcon
    },
    /**
     * Initialize intro for the current page
     *
     * @param {string} key index to identify the page
     * @param {number} delay delay time
     * @param {string} type intro type: 'hint' or 'intro'. Default 'intro'
     * @param {boolean} reset bypass key saved on localStorage
     * @returns void
     */
    setIntroJS({ key, delay = 10000, type = 'intro', reset = false }) {
      const prop = `introjs-${key}-finished`

      const hasFinished = reset ? false : Storage.getItem(prop)
      const isNotValidKey = !key
      const isHint = type === 'hint'

      this.intro_key = key
      this.intro_type = type

      if (isNotValidKey || hasFinished) return

      this.hideIntroJS()

      this.intro_timeout = setTimeout(() => {
        this.intro_status = isHint ? 'finished' : 'started'

        if (!isHint) document.body.classList.add('intro-started')

        if (!isHint) return this.startIntros(prop)

        if (reset) return this.resetHints(prop)

        this.startHints(prop)
      }, delay)
    },
    getHintOptions() {
      const i18n = this.$i18next
      const path = 'message.introJS.options'

      return {
        hintButtonLabel: i18n.t(`${path}.hintButtonLabel`),
        hintPosition: 'top-left',
      }
    },
    getIntroOptions() {
      const i18n = this.$i18next
      const path = 'message.introJS.options'

      return {
        nextLabel: i18n.t(`${path}.nextLabel`),
        prevLabel: i18n.t(`${path}.prevLabel`),
        doneLabel: i18n.t(`${path}.doneLabel`),
        stepNumbersOfLabel: i18n.t(`${path}.stepNumbersOfLabel`),
        disableInteraction: true,
        showStepNumbers: true,
        showBullets: false,
        showProgress: false,
        hidePrev: true,
        tooltipClass: 'introjs-video-page',
        highlightClass: 'introjs-video-page--helperLayer',
        scrollToElement: false,
      }
    },
    startHints(prop) {
      const options = this.getHintOptions()

      this.introJS()
        .setOptions(options)
        .onhintclose(() => this.saveToStorage(prop))
        .addHints()
    },
    resetHints(prop) {
      const options = this.getHintOptions()

      this.introJS()
        .setOptions(options)
        .onhintclose(() => this.saveToStorage(prop))
        .showHints()
    },
    startIntros(prop) {
      const options = this.getIntroOptions()

      this.introJS()
        .setOptions(options)
        .oncomplete(() => this.saveToStorage(prop))
        .onexit(() => {
          this.saveToStorage(prop)
          this.enablePageScrolling()
        })
        .onafterchange(() => {
          this.handleVideoPageIntroCss()
        })
        .onbeforechange((el) => {
          const introStep = el.getAttribute('data-step')
          this.handleScrollingPositioning(introStep, el)
        })
        .start()
    },
    hideIntroJS() {
      clearTimeout(this.intro_timeout)

      this.introJS().removeHints()
      this.introJS().exit()
    },
    saveToStorage(prop) {
      Storage.setItem(prop, true)
      document.body.classList.remove('intro-started')
      this.intro_status = 'finished'
    },
    refreshIntro() {
      this.setIntroJS({
        key: this.intro_key,
        type: this.intro_type,
        delay: 350,
        reset: true,
      })
    },
    disablePageScrolling() {
      const $document = document.querySelector('html')
      $document.style.cssText = 'overflow: hidden;'
    },
    enablePageScrolling() {
      const $document = document.querySelector('html')
      $document.removeAttribute('style')
    },
    handleScrollingPositioning(step, el) {
      const $settings = document.querySelector('.grid__settings')
      const $preview = document.querySelector('.grid__preview')
      if (step <= 4) {
        $settings.scroll(0, 0)
        $preview.scroll(0, 0)
      }
      if (step == 5) {
        el.scrollIntoView(true)
      }
      this.disablePageScrolling()
    },
  },
}
